import React, { useState, useMemo, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faCopy, faClock, faCompressAlt } from '@fortawesome/free-solid-svg-icons';
import HighlightedText, { countMatches } from './HighlightedText';

// --- Decompression utilities ---

async function tryDecompressBytes(bytes, hintEncoding) {
  const formats = [];
  const enc = (hintEncoding || '').toLowerCase().trim();
  if (enc === 'gzip' || enc === 'x-gzip') formats.push('gzip', 'deflate');
  else if (enc === 'deflate') formats.push('deflate', 'gzip');
  else formats.push('gzip', 'deflate');
  for (const fmt of formats) {
    try {
      if (typeof DecompressionStream === 'undefined') continue;
      const ds = new DecompressionStream(fmt);
      const writer = ds.writable.getWriter();
      const reader = ds.readable.getReader();
      writer.write(bytes); writer.close();
      const chunks = [];
      while (true) { const { done, value } = await reader.read(); if (done) break; chunks.push(value); }
      const totalLen = chunks.reduce((s, c) => s + c.length, 0);
      const result = new Uint8Array(totalLen);
      let offset = 0;
      for (const c of chunks) { result.set(c, offset); offset += c.length; }
      const text = new TextDecoder('utf-8').decode(result);
      if (text.length > 0 && !looksCompressed(text)) return { text, encoding: fmt };
    } catch {}
  }
  return null;
}

function looksCompressed(body) {
  if (!body || body.length < 10) return false;
  const sample = body.substring(0, 512);
  let n = 0;
  for (let i = 0; i < sample.length; i++) {
    const c = sample.charCodeAt(i);
    if (c < 32 && c !== 9 && c !== 10 && c !== 13) n++;
    if (c > 126 && c < 160) n++;
    if (c === 0xFFFD) n++;
  }
  return (n / sample.length) > 0.15;
}

function base64ToBytes(b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function garbledStringToBytes(str) {
  const bytes = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i++) bytes[i] = str.charCodeAt(i) & 0xFF;
  return bytes;
}

// --- Component ---

const ResponseViewer = ({ exchange, isDarkMode, searchRegex, activeMatchIndex, matchOffset = 0, onMatchCount, viewMode: viewModeProp, onViewModeChange }) => {
  const [viewModeState, setViewModeState] = useState('pretty');
  const viewMode = viewModeProp || viewModeState;
  const setViewMode = (m) => (onViewModeChange ? onViewModeChange(m) : setViewModeState(m));
  const [copied, setCopied] = useState(null); // 'body' | 'raw' | null
  const [decompressedBody, setDecompressedBody] = useState(null);
  const [decompressing, setDecompressing] = useState(false);
  const [decompressFormat, setDecompressFormat] = useState(null);
  const [decompressFailed, setDecompressFailed] = useState(false);
  const response = exchange?.response;

  const compressionInfo = useMemo(() => {
    if (!response) return null;
    const ceHeader = (response.headers || []).find(h => h.name.toLowerCase() === 'content-encoding');
    const encoding = ceHeader?.value;
    if (encoding && response.bodyEncoding === 'base64') return { type: 'base64', encoding };
    if (encoding && response.body && looksCompressed(response.body)) return { type: 'garbled', encoding };
    if (response.body && response.bodyEncoding !== 'base64' && looksCompressed(response.body)) return { type: 'garbled', encoding: 'gzip' };
    return null;
  }, [response]);

  useEffect(() => {
    if (!compressionInfo || decompressedBody || decompressing) return;
    let cancelled = false;
    setDecompressing(true); setDecompressFailed(false);
    (async () => {
      let bytes;
      if (compressionInfo.type === 'base64') { try { bytes = base64ToBytes(response.body); } catch {} }
      else bytes = garbledStringToBytes(response.body);
      if (bytes && bytes.length > 0) {
        const result = await tryDecompressBytes(bytes, compressionInfo.encoding);
        if (!cancelled) {
          if (result) { setDecompressedBody(result.text); setDecompressFormat(result.encoding); }
          else setDecompressFailed(true);
          setDecompressing(false);
        }
      } else if (!cancelled) { setDecompressFailed(true); setDecompressing(false); }
    })();
    return () => { cancelled = true; };
  }, [compressionInfo, response?.body, decompressedBody, decompressing]);

  useEffect(() => {
    setDecompressedBody(null); setDecompressFormat(null); setDecompressFailed(false); setDecompressing(false);
  }, [exchange?._id]);

  // Cap rendered body: highlighting a multi-MB body freezes the browser.
  const MAX_BODY = 200 * 1024;
  const fullBody = decompressedBody || response?.body || '';
  const bodyTooBig = fullBody.length > MAX_BODY;
  const effectiveBody = bodyTooBig ? fullBody.slice(0, MAX_BODY) : fullBody;
  const truncNote = bodyTooBig ? `\n\n… [body truncated — showing first ${Math.round(MAX_BODY / 1024)} KB of ${(fullBody.length / 1048576).toFixed(1)} MB]` : '';

  const buildRawResponse = () => {
    if (!response) return '';
    let raw = `HTTP/1.1 ${response.statusCode} ${response.statusText || ''}\r\n`;
    for (const h of (response.headers || [])) raw += `${h.name}: ${h.value}\r\n`;
    raw += '\r\n';
    raw += effectiveBody + truncNote;
    return raw;
  };

  const formatBody = () => {
    if (!effectiveBody) return '';
    if (bodyTooBig) return effectiveBody + truncNote;
    const ct = response?.contentType || '';
    if (ct.includes('json')) { try { return JSON.stringify(JSON.parse(effectiveBody), null, 2); } catch {} }
    return effectiveBody;
  };

  // Get displayed text for search counting
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const displayedText = useMemo(() => {
    if (!response) return '';
    if (viewMode === 'raw') return buildRawResponse();
    if (viewMode === 'headers') return (response.headers || []).map(h => `${h.name}: ${h.value}`).join('\n');
    return formatBody();
  }, [response, viewMode, effectiveBody]);

  const localMatchCount = useMemo(() => countMatches(displayedText, searchRegex), [displayedText, searchRegex]);

  useEffect(() => {
    if (onMatchCount) onMatchCount(localMatchCount);
  }, [localMatchCount, onMatchCount]);

  if (!response) {
    return (
      <div className={`p-4 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        No response available.
      </div>
    );
  }

  const statusColor = response.statusCode < 300 ? 'text-green-500' :
    response.statusCode < 400 ? 'text-blue-400' :
    response.statusCode < 500 ? 'text-yellow-500' : 'text-red-500';

  const preClass = `text-xs font-mono whitespace-pre-wrap ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;

  return (
    <div className="h-full flex flex-col">
      <div className={`flex items-center justify-between px-3 py-1.5 border-b ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faArrowDown} className="text-green-400 text-xs" />
          <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Response</span>
          <span className={`font-mono text-xs font-bold ${statusColor}`}>{response.statusCode}</span>
          {response.timing?.totalMs > 0 && (
            <span className={`text-[10px] flex items-center gap-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              <FontAwesomeIcon icon={faClock} /> {response.timing.totalMs}ms
            </span>
          )}
          <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {response.contentLength
              ? response.contentLength < 1024
                ? `${response.contentLength} B`
                : `${(response.contentLength / 1024).toFixed(1)} KB`
              : ''}
          </span>
          {decompressFormat && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-900/30 text-green-400 flex items-center gap-1">
              <FontAwesomeIcon icon={faCompressAlt} /> decoded {decompressFormat}
            </span>
          )}
          {decompressing && <span className="text-[10px] text-amber-400 animate-pulse">decompressing...</span>}
          {decompressFailed && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-900/30 text-red-400">
              compressed ({compressionInfo?.encoding}) — decode failed
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {['pretty', 'raw', 'headers', 'render'].map(mode => (
            <button key={mode} onClick={() => setViewMode(mode)}
              className={`px-2 py-0.5 text-[10px] rounded ${
                viewMode === mode
                  ? (isDarkMode ? 'bg-teal-900/30 text-teal-300' : 'bg-teal-100 text-teal-700')
                  : (isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600')
              }`}>{mode.charAt(0).toUpperCase() + mode.slice(1)}</button>
          ))}
          <div className="flex items-center gap-1 ml-2 border-l pl-2" style={{ borderColor: isDarkMode ? '#374151' : '#e5e7eb' }}>
            <button onClick={() => {
              navigator.clipboard.writeText(effectiveBody);
              setCopied('body'); setTimeout(() => setCopied(null), 1500);
            }}
              className={`px-1.5 py-0.5 rounded text-[10px] flex items-center gap-1 ${isDarkMode ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200' : 'text-gray-500 hover:bg-gray-200 hover:text-gray-700'}`}
              title="Copy response body">
              <FontAwesomeIcon icon={faCopy} /> {copied === 'body' ? '✓ Copied' : 'Body'}
            </button>
            <button onClick={() => {
              navigator.clipboard.writeText(buildRawResponse());
              setCopied('raw'); setTimeout(() => setCopied(null), 1500);
            }}
              className={`px-1.5 py-0.5 rounded text-[10px] flex items-center gap-1 ${isDarkMode ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200' : 'text-gray-500 hover:bg-gray-200 hover:text-gray-700'}`}
              title="Copy full response (headers + body)">
              <FontAwesomeIcon icon={faCopy} /> {copied === 'raw' ? '✓ Copied' : 'Raw'}
            </button>
          </div>
        </div>
      </div>

      <div className={`flex-1 overflow-auto p-3 ${viewMode === 'render' ? 'hidden' : ''}`}>
        {viewMode === 'raw' && (
          <HighlightedText text={displayedText} searchRegex={searchRegex}
            matchOffset={matchOffset} activeMatchIndex={activeMatchIndex} className={preClass} />
        )}
        {viewMode === 'pretty' && (
          <HighlightedText text={displayedText} searchRegex={searchRegex}
            matchOffset={matchOffset} activeMatchIndex={activeMatchIndex} className={preClass} />
        )}
        {viewMode === 'headers' && (
          <table className="w-full text-xs">
            <tbody>
              {(response.headers || []).map((h, i) => (
                <tr key={i} className={isDarkMode ? 'border-b border-gray-800' : 'border-b border-gray-100'}>
                  <td className={`py-0.5 pr-4 font-medium whitespace-nowrap ${isDarkMode ? 'text-teal-400' : 'text-teal-600'}`}>{h.name}</td>
                  <td className={`py-0.5 break-all ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{h.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {/* Render mode lives outside the padded/scroll wrapper so the iframe fills the panel.
          Sandboxed with no privileges: markup/CSS render but scripts/forms/network are blocked. */}
      {viewMode === 'render' && (
        <div className="flex-1 overflow-hidden bg-white">
          {(response?.contentType || '').includes('html') ? (
            <iframe
              title="Rendered response"
              sandbox=""
              srcDoc={effectiveBody}
              className="w-full h-full border-0 bg-white"
            />
          ) : (
            <div className={`p-4 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              Render is only available for HTML responses (content-type: {response?.contentType || 'unknown'}).
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ResponseViewer;
